import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { Dialog, Field, FIELD_CLASS } from '@/components/Dialog'
import { fetchClients } from '@/features/clients/api'
import { createProject, fetchDivisions } from '@/features/portfolio/api'
import type { ApiError } from '@/lib/api/client'
import type { Division, Project } from '@/types/domain'
import type { Client } from '@/types/support'

/**
 * Starting a project.
 *
 * Three fields, two of them required, because a project earns its detail once
 * it exists — the value, the dates, the workflow and the lead are all set on
 * the project itself, where they can be seen and argued with. A form that asks
 * for them up front is a form that gets abandoned.
 *
 * The key is not among them: it comes from the division's own sequence.
 */
export function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [divisionId, setDivisionId] = useState('')
  const [clientId, setClientId] = useState('')

  const divisions = useQuery<Division[], ApiError>({
    queryKey: ['divisions'],
    queryFn: fetchDivisions,
    retry: false,
  })

  const clients = useQuery<Client[], ApiError>({
    queryKey: ['clients'],
    queryFn: fetchClients,
    retry: false,
  })

  const create = useMutation<Project, ApiError, void>({
    mutationFn: () =>
      createProject({
        divisionId,
        name: name.trim(),
        clientId: clientId === '' ? null : clientId,
      }),
    onSuccess: (project) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['portfolio'] })
      void queryClient.invalidateQueries({ queryKey: ['divisions'] })
      // Straight into the project that was just made: the next thing anyone
      // does is put work in it.
      void navigate(`/projects/${project.key}`)
    },
  })

  const ready = name.trim().length > 0 && divisionId !== ''

  return (
    <Dialog title={t('portfolio.newProject')} onClose={onClose}>
      <form
        className="flex flex-col gap-3.5 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (ready) create.mutate()
        }}
      >
        <Field label={t('portfolio.projectName')}>
          <input
            value={name}
            autoFocus
            disabled={create.isPending}
            onChange={(event) => setName(event.target.value)}
            className={FIELD_CLASS}
          />
        </Field>

        <Field label={t('portfolio.division')}>
          <select
            value={divisionId}
            disabled={create.isPending}
            onChange={(event) => setDivisionId(event.target.value)}
            className={FIELD_CLASS}
          >
            <option value="">—</option>
            {(divisions.data ?? []).map((division) => (
              <option key={division.id} value={division.id}>
                {division.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t('portfolio.client')}>
          <select
            value={clientId}
            disabled={create.isPending}
            onChange={(event) => setClientId(event.target.value)}
            className={FIELD_CLASS}
          >
            <option value="">{t('portfolio.noClient')}</option>
            {(clients.data ?? []).map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </Field>

        {create.isError ? (
          <p className="text-[12px] text-[color:var(--color-state-blocked)]">
            {create.error.message}
          </p>
        ) : null}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!ready || create.isPending}
            className="bg-brand-600 rounded px-3.5 py-1.5 text-[13px] font-medium text-white disabled:opacity-40"
          >
            {create.isPending ? t('portfolio.creating') : t('portfolio.create')}
          </button>
        </div>
      </form>
    </Dialog>
  )
}
